/**
 * Logger Service
 *
 * Centralized logging utility for the application
 *
 * Features:
 * - Different log levels (info, warn, error)
 * - Environment-aware (development vs production)
 * - Structured logging with context
 * - Optional error monitoring integration
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Format log message with timestamp and context
 */
const formatMessage = (level, message, context = {}) => {
  const timestamp = new Date().toISOString();
  return {
    timestamp,
    level,
    message,
    ...context
  };
};

/**
 * Send error to monitoring service (placeholder for future integration)
 * Can be integrated with services like Sentry, LogRocket, etc.
 */
const sendToMonitoring = (level, message, error, context) => {
  // Placeholder for error monitoring service integration
  // Example: Sentry.captureException(error, { level, extra: context });
};

const logger = {
  /**
   * Log informational message
   * @param {string} message - Log message
   * @param {object} context - Additional context
   */
  info: (message, context = {}) => {
    if (isDevelopment) {
      const formatted = formatMessage('info', message, context);
      console.log(`[INFO] ${message}`, context);
    }
  },

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {object} context - Additional context
   */
  warn: (message, context = {}) => {
    const formatted = formatMessage('warn', message, context);
    console.warn(`[WARN] ${message}`, context);

    // In production, could send warnings to monitoring service
    if (!isDevelopment) {
      sendToMonitoring('warning', message, null, context);
    }
  },

  /**
   * Log error message
   * @param {string} message - Error message
   * @param {Error|object} error - Error object or additional context
   * @param {object} context - Additional context
   */
  error: (message, error = null, context = {}) => {
    const formatted = formatMessage('error', message, {
      ...context,
      error: error?.message || error,
      stack: error?.stack
    });

    console.error(`[ERROR] ${message}`, error, context);

    // In production, send errors to monitoring service
    if (!isDevelopment) {
      sendToMonitoring('error', message, error, context);
    }
  },

  /**
   * Log debug message (only in development)
   * @param {string} message - Debug message
   * @param {object} context - Additional context
   */
  debug: (message, context = {}) => {
    if (isDevelopment) {
      const formatted = formatMessage('debug', message, context);
      console.log(`[DEBUG] ${message}`, context);
    }
  }
};

export default logger;
